/**
 * @fileoverview Contact.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import TopImageBanner from '../components/tours/TopImageBanner'
import SEO from '../components/ui/SEO'
import { useTranslation } from 'react-i18next'

function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: '',
    name: '',
    email: '',
    subject: 'General Inquiry',
    message: ''
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      alert('Please agree to the Terms of Use and Privacy Policy before proceeding.');
      return;
    }
    alert('Thank you for contacting us. We will get back to you soon!');
  };

  return (
    <div className='flex flex-col w-full min-h-screen pt-24 md:pt-32 pb-20'>
      <SEO 
          title="Contact Us" 
          description="Get in touch with KITH Travels to plan your perfect Sri Lanka holiday. Reach our friendly team by email, phone, or visit us in person. We respond within 24 hours."
          keywords="contact KITH Travels, Sri Lanka travel enquiry, book Sri Lanka tour, travel agency contact Sri Lanka"
          schema={{
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact KITH Travels",
            "description": "Get in touch with KITH Travels to plan your perfect Sri Lanka holiday. Reach our friendly team by email, phone, or visit us in person. We respond within 24 hours.",
            "mainEntity": {
              "@type": "TravelAgency",
              "name": "KITH Travels",
              "telephone": "+94 70 32 72 573",
              "email": "info@kithtravels.com",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "No.295, 6th Lane, Pilomina Estate",
                "addressLocality": "Dondangoda",
                "addressCountry": "Sri Lanka"
              }
            }
          }}
      />
      {/* Hero Section */}
      <div className='px-[5%] md:px-[10%]'>
        <div className='relative w-full h-[40vh] md:h-[50vh]'>
          <TopImageBanner title={t('contact.hero_title')} />
        </div>
      </div>

      <div className='px-[5%] md:px-[10%] mt-20 grid grid-cols-1 lg:grid-cols-2 gap-16'>
        {/* Contact Info Column */}
        <div className='flex flex-col gap-12 text-left'>
            <div className='flex flex-col gap-4'>
                <h2 className='text-2xl md:text-4xl font-bold text-gray-800 leading-tight'>{t('contact.title_p1')} <span className='text-KITH-primary'>{t('contact.title_p2')}</span></h2>
                <p className='text-gray-500 text-sm md:text-base'>
                    {t('contact.desc')}
                </p>
            </div>

            <div className='flex flex-col gap-8'>
                {[
                    { icon: 'fi fi-br-envelope', label: t('contact.info.email'), value: 'info@kithtravels.com' },
                    { icon: 'fi fi-br-phone-call', label: t('contact.info.call'), value: '+94 70 32 72 573' },
                    { icon: 'fi fi-br-marker', label: t('contact.info.visit'), value: 'No.295, 6th Lane, Pilomina Estate, Dondangoda, Sri Lanka' }
                ].map((info, idx) => (
                    <div key={idx} className='flex gap-6 items-center group'>
                        <div className='w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100 shadow-sm group-hover:bg-KITH-primary group-hover:text-white transition-all duration-300'>
                            <i className={`${info.icon} text-xl`}></i>
                        </div>
                        <div className='flex flex-col'>
                            <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>{info.label}</span>
                            <span className='text-base md:text-lg font-bold text-gray-800'>{info.value}</span>
                        </div>
                    </div>
                ))}
            </div>

            <div className='flex flex-col gap-4 mt-4'>
                <span className='text-xs font-bold text-gray-400 uppercase tracking-widest'>{t('contact.follow')}</span>
                <div className='flex gap-4'>
                    {['facebook', 'instagram', 'twitter', 'linkedin'].map((social, idx) => (
                        <a key={idx} href="#" aria-label={`Follow KITH Travels on ${social.charAt(0).toUpperCase() + social.slice(1)}`} className='w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-KITH-primary hover:text-white transition-all duration-300'>
                            <i className={`fi fi-brands-${social} text-lg`}></i>
                        </a>
                    ))}
                </div>
            </div>
        </div>

        {/* Contact Form Column */}
        <div className='bg-white p-8 md:p-12 rounded-[3rem] shadow-2xl border border-gray-50 h-fit'>
            <form onSubmit={handleSubmit} className='flex flex-col gap-6'>
                <div className='flex flex-col gap-2'>
                    <label className='text-xs font-bold text-gray-500 uppercase ml-2'>{t('contact.form.title')}</label>
                    <div className='flex flex-wrap gap-3'>
                        {['Mr', 'Mrs', 'Ms', 'Dr'].map(t => (
                            <button 
                                key={t} type="button"
                                onClick={() => setFormData(prev => ({...prev, title: t}))}
                                className={`px-6 py-2 rounded-xl font-bold text-sm border-2 transition-all ${formData.title === t ? 'bg-KITH-primary border-KITH-primary text-white shadow-md' : 'bg-gray-50 border-transparent text-gray-500 hover:bg-gray-100'}`}
                            >{t}</button>
                        ))}
                    </div>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                    <div className='flex flex-col gap-2'>
                        <label className='text-xs font-bold text-gray-500 uppercase ml-2'>{t('contact.form.name')}</label>
                        <input type="text" name="name" onChange={handleInputChange} required placeholder="John Doe" className='p-4 bg-gray-50 rounded-2xl border border-transparent focus:border-KITH-primary outline-none transition-all text-sm' />
                    </div>
                    <div className='flex flex-col gap-2'>
                        <label className='text-xs font-bold text-gray-500 uppercase ml-2'>{t('contact.form.email')}</label>
                        <input type="email" placeholder="john@example.com" className='p-4 bg-gray-50 rounded-2xl border border-transparent focus:border-KITH-primary outline-none transition-all text-sm' />
                    </div>
                </div>
                
                <div className='flex flex-col gap-2'>
                    <label className='text-xs font-bold text-gray-500 uppercase ml-2'>{t('contact.form.subject')}</label>
                    <select className='p-4 bg-gray-50 rounded-2xl border border-transparent focus:border-KITH-primary outline-none transition-all text-sm appearance-none cursor-pointer'>
                        <option>{t('contact.form.subjects.general')}</option>
                        <option>{t('contact.form.subjects.booking')}</option>
                        <option>{t('contact.form.subjects.custom')}</option>
                        <option>{t('contact.form.subjects.partnership')}</option>
                    </select>
                </div>

                <div className='flex flex-col gap-2'>
                    <label className='text-xs font-bold text-gray-500 uppercase ml-2'>{t('contact.form.message')}</label>
                    <textarea rows="5" placeholder={t('contact.form.msg_placeholder')} className='p-4 bg-gray-50 rounded-2xl border border-transparent focus:border-KITH-primary outline-none transition-all text-sm resize-none'></textarea>
                </div>

                <div className='flex flex-col gap-6 mt-2'>
                    <label className="flex items-start gap-3 cursor-pointer select-none bg-KITH-primary/5 p-4 rounded-2xl border border-KITH-primary/20">
                        <input 
                            type="checkbox" 
                            checked={agreedToTerms} 
                            onChange={(e) => setAgreedToTerms(e.target.checked)} 
                            className="mt-1 rounded border-gray-300 text-KITH-primary focus:ring-KITH-primary w-5 h-5"
                            required
                        />
                        <span className="text-sm text-gray-700 leading-relaxed font-medium">
                            {t('contact.form.agree_part1')} <Link to="/terms-and-conditions" target="_blank" className="text-KITH-primary hover:underline font-bold">{t('nav.terms_and_conditions')}</Link> {t('contact.form.agree_part2')} <Link to="/privacy-policy" target="_blank" className="text-KITH-primary hover:underline font-bold">{t('nav.privacy_policy')}</Link>.
                        </span>
                    </label>
                </div>

                <button onClick={handleSubmit} className='mt-4 py-5 bg-KITH-primary text-white font-bold rounded-2xl hover:bg-KITH-primary-dark transition-all shadow-lg hover:shadow-KITH-primary/20 transform hover:-translate-y-1 active:scale-95'>
                    {t('contact.form.button')}
                </button>
            </form>
        </div>
      </div>

      {/* Map Section */}
      <div className='px-[5%] md:px-[10%] mt-32'>
        <div className='w-full h-[400px] bg-gray-100 rounded-[3rem] overflow-hidden relative group border border-gray-100 shadow-md'>
            <iframe 
                title="KITH Travels Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d31697.551694200424!2d80.00762961520692!3d6.586111874213123!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae22e118320bbcb%3A0x868cb5a3d7e5d800!2sDodangoda%2C%20Sri%20Lanka!5e0!3m2!1sen!2slk!4v1716300000000!5m2!1sen!2slk" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
        </div>
      </div>
    </div>
  )
}

export default Contact
